import * as React from "react";
import * as React44ms from "../../../Util/form-utils/react-44ms";
import * as ReactQuery from "react-query";
import { UseQueryResult } from "react-query";
import axios from "axios";

import {
  GraphqlWrappedData,
  InitialStateMinimumRequirements_GraphQl,
} from "../../graphql";

import { useCustomerSession } from "../customer-session-provider";

import { CustomerInformationQuery__QueryKey } from "./customer-information-query-key-factory";

export interface CustomerInformation_Response {
  firstname?: string;
  lastname?: string;
  suffix?: string;
  email?: string;
  addresses?: CustomerInformation_Address_Response[];
}
export interface CustomerInformation_Address_Response {
  id: any;
  firstname?: string;
  lastname?: string;
  street?: string[];
  city?: string;
  region?: {
    region_id?: string;
    region_code?: string;
    region?: string;
  };
  postcode?: string;
  country_code?: Uppercase<string>;
  telephone?: string;
  default_shipping?: boolean;
  default_billing?: boolean;
}

export interface CustomerInformationQuery_ConsumerInterface {
  data: UseQueryResult<CustomerInformation_Response>["data"];
  status: UseQueryResult<CustomerInformation_Response>["status"];
}
const defaultValue =
  {} as unknown as CustomerInformationQuery_ConsumerInterface;
const CustomerInformationQuery_Context = React.createContext(defaultValue);
const CustomerInformationQuery_Context_44 = React44ms.React.createContext(
  defaultValue,
  CustomerInformationQuery_Context
);

export interface InitialStateMinimumRequirements_CustomerInformation
  extends InitialStateMinimumRequirements_GraphQl {}

export interface CustomerInformationQuery_ProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_CustomerInformation;
  };
}
export const CustomerInformationQuery_Provider: React.FunctionComponent<
  CustomerInformationQuery_ProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const { isCustomerLoggedIn } = useCustomerSession();

  const queryKey = React.useMemo(() => {
    return CustomerInformationQuery__QueryKey.customerInformation();
  }, []);

  const queryFn: ReactQuery.QueryFunction<
    CustomerInformation_Response,
    typeof queryKey
  > = async (context: ReactQuery.QueryFunctionContext<typeof queryKey>) => {
    let gqlQuery = `{
      customer {
        firstname
        lastname
        suffix
        email

        addresses {
          id
          firstname
          lastname
          street
          city
          region {
            region_id
            region_code
            region
          }
          postcode
          country_code
          telephone
          default_shipping
          default_billing
        }
      }
    }`;

    const headers = {
      "Content-Type": "application/json",
      Store: initialState.store_code,
    };

    const { data } = await axios.post<
      GraphqlWrappedData<"customer", CustomerInformation_Response>
    >("/graphql", { query: gqlQuery }, { headers });

    const { data: gqlResponseData } = data || {};
    const { customer } = gqlResponseData || {};

    return customer;
  };

  const { status, data, ...result } = ReactQuery.useQuery(queryKey, queryFn, {
    enabled: isCustomerLoggedIn,
    refetchOnWindowFocus: false,
  });

  const value: CustomerInformationQuery_ConsumerInterface = React.useMemo(
    () =>
      isCustomerLoggedIn
        ? {
            status,
            data,
          }
        : {
            data: undefined,
            status: "success",
          },
    [isCustomerLoggedIn, status, data]
  );

  return (
    <CustomerInformationQuery_Context.Provider value={value}>
      <CustomerInformationQuery_Context_44.Provider value={value}>
        {props?.children || []}
      </CustomerInformationQuery_Context_44.Provider>
    </CustomerInformationQuery_Context.Provider>
  );
};

export const useCustomerInformationQuery = () => {
  const context = React.useContext(CustomerInformationQuery_Context);

  /**
   * Normally we do this pattern below for using any provider,
   * this here is a special case where
   * we want the defaultValue to be used when this provider isn't in the tree,
   * because it can be used when there is a fully formed request information packet,
   * which isn't guaranteed to be available immediately.
   */
  // if (context === defaultValue) {
  //   throw new Error(
  //     "useDataSourceCheckoutRegions must be used within a CustomerInformationQuery_Provider"
  //   );
  // }

  return context;
};

export const useCustomerInformationQuery_44 = () => {
  const context = React44ms.React.useContext(
    CustomerInformationQuery_Context_44
  );

  /**
   * Normally we do this pattern below for using any provider,
   * this here is a special case where
   * we want the defaultValue to be used when this provider isn't in the tree,
   * because it can be used when there is a fully formed request information packet,
   * which isn't guaranteed to be available immediately.
   */
  // if (context === defaultValue) {
  //   throw new Error(
  //     "useDataSourceCheckoutRegions must be used within a CustomerInformationQuery_Provider"
  //   );
  // }

  return context;
};
